import {
    Building2, Globe, Linkedin, MapPin, Users, Calendar, Mail, Phone,
    ShieldCheck, AlertTriangle, Server, FileText, CheckCircle2, Circle,
    SlidersHorizontal, Image as ImageIcon,
} from 'lucide-react';
import { API_CONFIG } from '@/config/config.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * Read-only view of what a company has configured for itself.
 *
 * Support-facing: it answers "have they set this up, and is it working?" without
 * exposing anything the company would not expect platform staff to read. The
 * SMTP password is never sent by the API, and template wording is summarised as
 * a count rather than shown.
 */

const EMAIL_STATES = {
    verified: {
        label: 'Active', icon: ShieldCheck,
        cls: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
        blurb: 'Candidate emails are sent from their own mail server.',
    },
    unverified: {
        label: 'Saved, not verified', icon: AlertTriangle,
        cls: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
        blurb: 'Settings stored but never tested — still sending as EchoHire.',
    },
    failing: {
        label: 'Not working', icon: AlertTriangle,
        cls: 'border-destructive/25 bg-destructive/10 text-destructive',
        blurb: 'Their mail server rejected a message — falling back to EchoHire.',
    },
    unconfigured: {
        label: 'Using EchoHire', icon: Server,
        cls: 'border-border bg-muted text-muted-foreground',
        blurb: 'No mail server configured.',
    },
};

function Ring({ percent, size = 60 }) {
    const stroke = 5;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    return (
        <svg width={size} height={size} className="-rotate-90 shrink-0" role="img"
            aria-label={`Setup ${percent}% complete`}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round"
                className={percent >= 100 ? 'stroke-emerald-500' : 'stroke-primary'}
                strokeDasharray={c} strokeDashoffset={c - (c * percent) / 100} />
            <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
                className="rotate-90 fill-foreground text-xs font-semibold"
                style={{ transformOrigin: 'center' }}>{percent}%</text>
        </svg>
    );
}

/** One profile field. Empty values are shown explicitly — a gap is information. */
function Row({ icon: Icon, label, value, href }) {
    const empty = !value;
    return (
        <div className="flex items-start gap-2.5 py-2">
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
                <p className="text-[11px] text-muted-foreground">{label}</p>
                {empty ? (
                    <p className="text-xs italic text-muted-foreground/50">Not provided</p>
                ) : href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer nofollow"
                        className="block truncate text-xs font-medium text-primary hover:underline">{value}</a>
                ) : (
                    <p className="break-words text-xs font-medium text-foreground">{value}</p>
                )}
            </div>
        </div>
    );
}

const CHECK_LABELS = {
    identity:  'Name and description',
    logo:      'Logo uploaded',
    smtp:      'Own mail server active',
    templates: 'Email wording customised',
    threshold: 'Pass threshold set',
};

export default function CompanyProfilePanel({ company, setup }) {
    const p = company.profile || {};
    const cfg = company.emailConfig || {};
    const state = EMAIL_STATES[setup?.emailStatus] || EMAIL_STATES.unconfigured;
    const logoSrc = p.logoUrl
        ? (p.logoUrl.startsWith('http') ? p.logoUrl : `${API_CONFIG.STATIC_BASE_URL}${p.logoUrl}`)
        : null;

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

            {/* ── Setup progress ────────────────────────────────────────── */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Setup progress</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                        <Ring percent={setup?.percent ?? 0} />
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {setup?.done ?? 0} of {setup?.total ?? 5} complete
                            </p>
                            <p className="text-xs text-muted-foreground">
                                The same figure the company sees in its own settings.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-1 border-t border-border pt-3">
                        {Object.entries(setup?.checks || {}).map(([key, done]) => (
                            <div key={key} className="flex items-center gap-2 py-0.5">
                                {done
                                    ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                    : <Circle className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />}
                                <span className={cn('text-xs', done ? 'text-foreground' : 'text-muted-foreground')}>
                                    {CHECK_LABELS[key] || key}
                                </span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* ── Mail server ───────────────────────────────────────────── */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Mail server</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className={cn('flex items-start gap-2.5 rounded-lg border px-3 py-2.5', state.cls)}>
                        <state.icon className="mt-0.5 h-4 w-4 shrink-0" />
                        <div className="min-w-0">
                            <p className="text-sm font-semibold">{state.label}</p>
                            <p className="text-xs opacity-90">{state.blurb}</p>
                        </div>
                    </div>

                    {setup?.emailStatus !== 'unconfigured' && (
                        <div className="divide-y divide-border">
                            <Row icon={Server} label="SMTP host"
                                value={cfg.host ? `${cfg.host}:${cfg.port}${cfg.secure ? ' (SSL)' : ''}` : ''} />
                            <Row icon={Mail} label="Username" value={cfg.authUser} />
                            <Row icon={Mail} label="Sends from"
                                value={cfg.fromName ? `${cfg.fromName} <${cfg.fromEmail || cfg.authUser}>` : cfg.fromEmail} />
                            <Row icon={ShieldCheck} label="Last verified"
                                value={cfg.lastTestedAt ? new Date(cfg.lastTestedAt).toLocaleString() : ''} />
                            {cfg.lastError && (
                                <div className="py-2">
                                    <p className="text-[11px] text-muted-foreground">Last error</p>
                                    <p className="text-xs font-medium text-destructive">{cfg.lastError}</p>
                                </div>
                            )}
                        </div>
                    )}

                    <p className="border-t border-border pt-2.5 text-[11px] leading-relaxed text-muted-foreground">
                        The SMTP password is encrypted and never returned by the API —
                        not to you either.
                    </p>
                </CardContent>
            </Card>

            {/* ── Company profile ───────────────────────────────────────── */}
            <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Company profile</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-4 flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-3">
                        {logoSrc ? (
                            <img src={logoSrc} alt="" className="h-12 w-12 shrink-0 rounded-lg border border-border bg-background object-contain p-1" />
                        ) : (
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
                                <ImageIcon className="h-5 w-5 text-muted-foreground/50" />
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-foreground">{company.companyName}</p>
                            <p className="text-xs text-muted-foreground">
                                {logoSrc ? 'Logo uploaded' : 'No logo — candidates see initials'}
                            </p>
                        </div>
                    </div>

                    <div className="mb-3">
                        <p className="text-[11px] text-muted-foreground">About us</p>
                        {p.about
                            ? <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-foreground">{p.about}</p>
                            : <p className="mt-1 text-xs italic text-muted-foreground/50">Not provided — job pages show no company context.</p>}
                    </div>

                    <div className="grid gap-x-6 border-t border-border pt-2 sm:grid-cols-2 lg:grid-cols-3">
                        <Row icon={Globe}    label="Website"  value={p.website}  href={p.website} />
                        <Row icon={Linkedin} label="LinkedIn" value={p.linkedIn} href={p.linkedIn} />
                        <Row icon={Building2} label="Industry" value={p.industry} />
                        <Row icon={Users}    label="Company size" value={p.companySize ? `${p.companySize} employees` : ''} />
                        <Row icon={MapPin}   label="Location" value={p.location} />
                        <Row icon={Calendar} label="Founded"  value={p.foundedYear} />
                        <Row icon={Mail}     label="Public contact email" value={p.publicContactEmail} />
                        <Row icon={Phone}    label="Public phone" value={p.publicPhone} />
                    </div>
                </CardContent>
            </Card>

            {/* ── Emails ────────────────────────────────────────────────── */}
            <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Candidate emails</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-3">
                    <div className="flex items-center gap-2.5">
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {setup?.templateStats?.customised ?? 0} of {setup?.templateStats?.total ?? 7} customised
                            </p>
                            <p className="text-[11px] text-muted-foreground">Wording is theirs and not shown here</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {company.notifyOnReject === false ? 'Off' : 'On'}
                            </p>
                            <p className="text-[11px] text-muted-foreground">Rejected candidates notified</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {company.interviewSettings?.passThreshold ?? 70}%
                            </p>
                            <p className="text-[11px] text-muted-foreground">Interview pass threshold</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
